// sentItemsFixtures — a web-side corpus of the W0 contract, for render tests
// and for the dev-only `?fixtures=1` mode on /sent-items.
//
// This is NOT a second contract. Every shape here is the exported type from
// sentItemsClient.ts, so a field that drifts on the server side fails to
// compile here before it ever renders wrong. The server has its own fixtures
// (apps/api/src/lib/sentItems/fixtures.ts, W0) that the API tests use; these
// exist because the SPA is being built against routes W2 is writing at the
// same time, and a page you cannot look at is a page nobody reviews.
//
// The corpus is chosen to cover the shapes that have historically gone wrong
// rather than the shapes that are easy:
//
//   • a lease OUT whose newest version is an unsent revision while an older
//     submission is still live (T3 — one row, not two);
//   • an intake whose three sections are in three different states (§5.5) —
//     including one never requested, which is SHOWN muted, not hidden;
//   • a DRAFT, which has no recipients and no activity at all (T14) — the
//     panel must render neither block rather than two empty ones;
//   • a fully-executed lease whose occupancy upload FAILED (§5.3) — executed
//     but still in motion, and blocked;
//   • a condition report before any send, whose state is a system phase
//     ("Pulling from zInspector") and not a row of its own (Q3, T13);
//   • an accommodation row, and the withheld-stub row that stands in for one
//     when the viewer is off the roster (§5.7, T5).
//
// `FIXTURE_NOW` freezes what "2 days ago" means, so a relative-time assertion
// is a real assertion rather than a clock reading.

import type {
  DocActivityEvent,
  DocRecipient,
  ViewDocPanel,
} from '@/lib/viewDocsClient';
import type {
  IntakeSectionDetail,
  IntakeSectionSummary,
  ItemAction,
  ItemVersion,
  SentItemDetailResponse,
  SentItemRow,
  SentItemsListResponse,
} from '@/lib/sentItemsClient';

/** The instant every fixture timestamp is measured back from. */
export const FIXTURE_NOW = new Date('2026-08-28T17:00:00.000Z');

function ago(ms: number): string {
  return new Date(FIXTURE_NOW.getTime() - ms).toISOString();
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const NO_SIGNALS = { block: 0, attention: 0, notice: 0 };

// ---------------------------------------------------------------------------
// 1 — A lease OUT with three versions, the newest of them an unsent revision
// ---------------------------------------------------------------------------

const LEASE_OUT_ID = 'lease:9f2c1a80-1111-4aa1-9c11-0a7f8cbe0001';

export const leaseOutRow: SentItemRow = {
  itemId: LEASE_OUT_ID,
  kind: 'lease',
  kindLabel: 'Lease',
  typeGroup: 'lease',
  subject: 'Dana Fairweather, Marcus Fairweather',
  propertyLabel: '108 SE 6th St',
  unitLabel: '#8',
  caseId: 'case-fairweather',
  caseKind: 'household',
  groupId: '9f2c1a80-1111-4aa1-9c11-0a7f8cbe0001',
  state: 'in_progress',
  stateLabel: '1 of 4 signed',
  lifecycle: { step: 'out', label: 'Out for signature' },
  waitingOn: {
    party: 'recipient',
    names: ['Marcus Fairweather'],
    label: 'Marcus Fairweather · 2 days',
  },
  since: ago(2 * DAY),
  inMotion: true,
  lastActivityAt: ago(3 * HOUR),
  sentAt: ago(2 * DAY),
  completedAt: null,
  signals: { block: 0, attention: 1, notice: 0 },
  digest: '12 months from 1 Sep 2026 · $1,850/mo · 2 residents · 1 animal',
  versionCount: 3,
  viewable: true,
  resendable: true,
};

// A version's `docId` addresses the GROUP, not the version: the /api/docs
// loader keys signature documents on the groupId (a W-9 on its versionId, T25)
// and a version is selected with `?version=<ItemVersion.id>`. Composing the
// version into the id — `<groupId>:v3` — produces something no loader resolves.
// (W1, verified against projectSignatureDocument, 2026-08-28.)
const leaseOutVersions: ItemVersion[] = [
  {
    id: 'v3',
    docKind: 'lease',
    docId: leaseOutRow.groupId!,
    label: 'Version 3 — revision, not sent',
    state: 'draft',
    stateLabel: 'Draft',
    createdAt: ago(3 * HOUR),
    current: true,
    viewable: true,
  },
  {
    id: 'v2',
    docKind: 'lease',
    docId: leaseOutRow.groupId!,
    label: 'Version 2',
    state: 'in_progress',
    stateLabel: '1 of 4 signed',
    createdAt: ago(2 * DAY),
    sentAt: ago(2 * DAY),
    current: false,
    stillOpen: true,
    viewable: true,
  },
  {
    id: 'v1',
    docKind: 'lease',
    docId: leaseOutRow.groupId!,
    label: 'Version 1',
    state: 'superseded',
    stateLabel: 'Superseded',
    createdAt: ago(6 * DAY),
    sentAt: ago(6 * DAY),
    current: false,
    viewable: true,
  },
];

const leaseOutRecipients: DocRecipient[] = [
  {
    id: 'signer-dana',
    name: 'Dana Fairweather',
    email: 'dana@example.com',
    role: 'Resident',
    roleLabel: 'Resident',
    status: 'completed',
    statusLabel: 'Signed',
    canResend: false,
  },
  {
    id: 'signer-marcus',
    name: 'Marcus Fairweather',
    email: 'marcus@example.com',
    role: 'Resident',
    roleLabel: 'Resident',
    status: 'opened',
    statusLabel: 'Opened',
    canResend: true,
    resendId: 'lease:9f2c1a80-1111-4aa1-9c11-0a7f8cbe0001:signer-marcus',
  },
  {
    id: 'signer-broker',
    name: 'Miles Scruggs',
    role: 'Broker',
    roleLabel: 'Broker',
    status: 'sent',
    statusLabel: 'Waiting',
    canResend: false,
  },
];

const leaseOutPanel: ViewDocPanel = {
  digest: leaseOutRow.digest,
  groups: [
    {
      rows: [
        { label: 'Property', value: '108 SE 6th St #8' },
        { label: 'Term', value: '1 Sep 2026 – 31 Aug 2027' },
        { label: 'Rent', value: '$1,850 / month', emphasis: true },
        { label: 'Security deposit', value: '$1,850' },
      ],
    },
    {
      heading: 'Residents',
      rows: [
        { label: 'Resident', value: 'Dana Fairweather' },
        { label: 'Resident', value: 'Marcus Fairweather' },
        { label: 'Animals', value: 'Rex — dog, 40 lb' },
      ],
    },
  ],
};

const leaseOutActivity: DocActivityEvent[] = [
  { at: ago(6 * DAY), type: 'sent', kind: 'sent', text: 'Sent for signature' },
  {
    at: ago(2 * DAY),
    type: 'sent',
    kind: 'sent',
    text: 'Revision sent for signature',
  },
  {
    at: ago(2 * DAY - HOUR),
    type: 'opened',
    kind: 'email_opened',
    text: 'Opened the email',
    recipientName: 'Dana Fairweather',
  },
  {
    at: ago(DAY),
    type: 'signed',
    kind: 'signed',
    text: 'Signed',
    recipientName: 'Dana Fairweather',
  },
  {
    at: ago(5 * HOUR),
    type: 'reminded',
    kind: 'reminded',
    text: 'Reminder sent',
    recipientName: 'Marcus Fairweather',
  },
  {
    at: ago(3 * HOUR),
    type: 'page_opened',
    kind: 'page_opened',
    text: 'Opened the signing page',
    recipientName: 'Marcus Fairweather',
  },
];

const leaseOutActions: ItemAction[] = [
  {
    id: 'remind',
    label: 'Remind',
    method: 'POST',
    endpoint: '/api/requests/resend',
    body: { recipient: 'lease:9f2c1a80-1111-4aa1-9c11-0a7f8cbe0001:signer-marcus' },
  },
  {
    id: 'send-revision',
    label: 'Send revision',
    method: 'POST',
    endpoint: '/api/leases/9f2c1a80-1111-4aa1-9c11-0a7f8cbe0001/send',
    body: { confirmNewSend: true },
    tone: 'primary',
  },
  {
    id: 'builder',
    label: 'Open in builder',
    method: 'navigate',
    endpoint: '/applications/9f2c1a80-1111-4aa1-9c11-0a7f8cbe0001/lease',
  },
  {
    id: 'void',
    label: 'Void',
    method: 'POST',
    endpoint: '/api/leases/9f2c1a80-1111-4aa1-9c11-0a7f8cbe0001/void',
    tone: 'danger',
    confirm:
      'Voiding withdraws the document from everyone who has it. Signatures already collected are discarded and cannot be recovered.',
  },
];

export const leaseOutDetail: SentItemDetailResponse = {
  row: leaseOutRow,
  panel: leaseOutPanel,
  file: {
    available: true,
    url: '/api/docs/lease/9f2c1a80-1111-4aa1-9c11-0a7f8cbe0001/file?version=v3',
    filename: 'lease-fairweather.pdf',
  },
  versions: leaseOutVersions,
  selectedVersionId: 'v3',
  recipients: leaseOutRecipients,
  activity: leaseOutActivity,
  pending: ['Marcus Fairweather — has not signed', 'Miles Scruggs — countersignature'],
  fillOnBehalf: false,
  actions: leaseOutActions,
  rail: [
    { state: 'prepared', label: 'Prepared', status: 'past' },
    { state: 'sent', label: 'Sent', status: 'past' },
    { state: 'signing', label: 'Signing', status: 'current' },
    { state: 'countersign', label: 'Countersign', status: 'future' },
    { state: 'executed', label: 'Executed', status: 'future' },
  ],
};

// ---------------------------------------------------------------------------
// 2 — One intake row, three sections, three different states
// ---------------------------------------------------------------------------

const INTAKE_ID = 'intake:7c11e3b0-2222-4bb2-8d22-0a7f8cbe0002';
const intakeGroupId = '7c11e3b0-2222-4bb2-8d22-0a7f8cbe0002';

const intakeSections: IntakeSectionSummary[] = [
  {
    section: 'designation',
    label: 'Household designee',
    status: 'completed',
    requestedAt: ago(9 * DAY),
    completedAt: ago(7 * DAY),
    docKind: 'intake_designation',
    docId: intakeGroupId,
  },
  {
    section: 'animals',
    label: 'Animals',
    status: 'requested',
    requestedAt: ago(9 * DAY),
    docKind: 'intake_animals',
    docId: intakeGroupId,
  },
  {
    section: 'yard_care',
    label: 'Yard care',
    status: 'not_requested',
    docKind: 'intake_yard_care',
    docId: intakeGroupId,
  },
];

export const intakeRow: SentItemRow = {
  itemId: INTAKE_ID,
  kind: 'intake',
  kindLabel: 'Intake',
  typeGroup: 'intake',
  subject: 'Priya Raman',
  propertyLabel: '2210 W Poplar',
  unitLabel: null,
  caseId: 'case-raman',
  caseKind: 'household',
  groupId: intakeGroupId,
  state: 'in_progress',
  stateLabel: '1 of 2 answered',
  lifecycle: { step: 'out', label: 'With the applicant' },
  waitingOn: { party: 'recipient', names: ['Priya Raman'], label: 'Priya Raman · 9 days' },
  since: ago(9 * DAY),
  inMotion: true,
  lastActivityAt: ago(7 * DAY),
  sentAt: ago(9 * DAY),
  completedAt: null,
  signals: { block: 0, attention: 0, notice: 1 },
  digest: 'Designee complete · Animals open · Yard care not requested',
  versionCount: 0,
  viewable: false,
  resendable: true,
  sections: intakeSections,
};

const intakeSectionDetails: IntakeSectionDetail[] = [
  {
    ...intakeSections[0]!,
    panel: {
      digest: 'Priya Raman is the household designee',
      groups: [{ rows: [{ label: 'Designee', value: 'Priya Raman' }] }],
    },
    replica: {
      sections: [
        {
          heading: 'Household designee',
          status: 'completed',
          fields: [{ label: 'Who speaks for the household?', kind: 'text', value: 'Priya Raman' }],
        },
      ],
    },
  },
  {
    ...intakeSections[1]!,
    panel: {
      digest: 'Not yet answered',
      groups: [{ rows: [{ label: 'Animals', value: 'Not yet answered', pending: true }] }],
    },
    replica: {
      sections: [
        {
          heading: 'Your animals',
          status: 'requested',
          fields: [
            { label: 'Do you have any animals?', kind: 'choice', options: ['Yes', 'No'] },
          ],
        },
      ],
    },
  },
  {
    ...intakeSections[2]!,
    panel: {
      digest: 'Not requested',
      groups: [{ rows: [{ label: 'Yard care', value: 'Not requested', pending: true }] }],
    },
  },
];

export const intakeDetail: SentItemDetailResponse = {
  row: intakeRow,
  panel: {
    digest: intakeRow.digest,
    groups: [
      {
        rows: [
          { label: 'Household designee', value: 'Priya Raman' },
          { label: 'Animals', value: 'Not yet answered', pending: true },
          { label: 'Yard care', value: 'Not requested', pending: true },
        ],
      },
    ],
  },
  versions: [],
  selectedVersionId: '',
  recipients: [
    {
      id: 'applicant-priya',
      name: 'Priya Raman',
      email: 'priya@example.com',
      role: 'Applicant',
      roleLabel: 'Applicant',
      status: 'opened',
      statusLabel: 'Opened',
      canResend: true,
      resendId: `intake_animals:${intakeGroupId}:applicant-priya`,
    },
  ],
  activity: [
    { at: ago(9 * DAY), type: 'sent', kind: 'sent', text: 'Intake requested' },
    {
      at: ago(7 * DAY),
      type: 'responded',
      kind: 'responded',
      text: 'Answered the household designee',
      recipientName: 'Priya Raman',
    },
  ],
  pending: ['Animals — not yet answered'],
  fillOnBehalf: true,
  actions: [
    {
      id: 'resend',
      label: 'Send again',
      method: 'POST',
      endpoint: '/api/requests/resend',
      body: { recipient: `intake_animals:${intakeGroupId}:applicant-priya` },
    },
  ],
  sections: intakeSectionDetails,
};

// ---------------------------------------------------------------------------
// 3 — A finished lease: executed, synced, nothing waiting on anyone
// ---------------------------------------------------------------------------

const LEASE_DONE_ID = 'lease:5a09b7c0-3333-4cc3-9e33-0a7f8cbe0003';

export const leaseDoneRow: SentItemRow = {
  itemId: LEASE_DONE_ID,
  kind: 'lease',
  kindLabel: 'Renewal',
  typeGroup: 'lease',
  isRenewal: true,
  subject: 'Alan Whitmore',
  propertyLabel: '14 W Alder',
  unitLabel: '#3',
  caseId: 'case-whitmore',
  caseKind: 'household',
  groupId: 'renewal-01J9ZQ2K3M',
  state: 'completed',
  stateLabel: 'Fully executed',
  lifecycle: { step: 'synced', label: 'Filed in the portal' },
  waitingOn: null,
  since: null,
  inMotion: false,
  lastActivityAt: ago(16 * DAY),
  sentAt: ago(24 * DAY),
  completedAt: ago(16 * DAY),
  signals: NO_SIGNALS,
  digest: '12 months from 1 Oct 2026 · $1,620/mo',
  versionCount: 1,
  viewable: true,
  resendable: false,
};

export const leaseDoneDetail: SentItemDetailResponse = {
  row: leaseDoneRow,
  panel: {
    digest: leaseDoneRow.digest,
    groups: [
      {
        rows: [
          { label: 'Property', value: '14 W Alder #3' },
          { label: 'Term', value: '1 Oct 2026 – 30 Sep 2027' },
          { label: 'Rent', value: '$1,620 / month', emphasis: true },
        ],
      },
      {
        heading: 'Signatures',
        rows: [
          {
            label: 'Resident',
            value: 'Alan Whitmore',
            signature: { signedAt: '12 Aug 2026' },
          },
          {
            label: 'Broker',
            value: 'Miles Scruggs',
            signature: { signedAt: '12 Aug 2026' },
          },
        ],
      },
    ],
  },
  file: {
    available: true,
    url: '/api/docs/lease/renewal-01J9ZQ2K3M/file',
    filename: 'renewal-whitmore.pdf',
  },
  versions: [
    {
      id: 'v1',
      docKind: 'lease',
      docId: 'renewal-01J9ZQ2K3M',
      label: 'Version 1',
      state: 'completed',
      stateLabel: 'Fully executed',
      createdAt: ago(24 * DAY),
      sentAt: ago(24 * DAY),
      current: true,
      viewable: true,
    },
  ],
  selectedVersionId: 'v1',
  recipients: [
    {
      id: 'signer-alan',
      name: 'Alan Whitmore',
      role: 'Resident',
      roleLabel: 'Resident',
      status: 'completed',
      statusLabel: 'Signed',
      canResend: false,
    },
  ],
  activity: [
    { at: ago(24 * DAY), type: 'sent', kind: 'sent', text: 'Sent for signature' },
    {
      at: ago(17 * DAY),
      type: 'signed',
      kind: 'signed',
      text: 'Signed',
      recipientName: 'Alan Whitmore',
    },
    { at: ago(16 * DAY), type: 'completed', kind: 'completed', text: 'Fully executed' },
  ],
  pending: [],
  fillOnBehalf: false,
  actions: [
    {
      id: 'download',
      label: 'Download',
      method: 'GET',
      endpoint: '/api/docs/lease/renewal-01J9ZQ2K3M/file',
    },
  ],
};

// ---------------------------------------------------------------------------
// 4 — A draft: no recipients, no activity, no sentAt (T14)
// ---------------------------------------------------------------------------

const LEASE_DRAFT_ID = 'lease:3d77f210-4444-4dd4-8f44-0a7f8cbe0004';

export const leaseDraftRow: SentItemRow = {
  itemId: LEASE_DRAFT_ID,
  kind: 'lease',
  kindLabel: 'Lease',
  typeGroup: 'lease',
  subject: 'Teodora Nunez',
  propertyLabel: '905 N Colville',
  unitLabel: null,
  caseId: null,
  caseKind: null,
  groupId: '3d77f210-4444-4dd4-8f44-0a7f8cbe0004',
  state: 'draft',
  stateLabel: 'Resume draft',
  lifecycle: { step: 'drafting', label: 'Being prepared' },
  waitingOn: { party: 'whp', names: [], label: 'WHP · 5 hours' },
  since: ago(5 * HOUR),
  inMotion: true,
  lastActivityAt: ago(5 * HOUR),
  sentAt: null,
  completedAt: null,
  signals: NO_SIGNALS,
  digest: 'Started 28 Aug · term and rent not yet set',
  versionCount: 0,
  viewable: false,
  resendable: false,
};

export const leaseDraftDetail: SentItemDetailResponse = {
  row: leaseDraftRow,
  panel: {
    digest: leaseDraftRow.digest,
    groups: [
      {
        rows: [
          { label: 'Property', value: '905 N Colville' },
          { label: 'Term', value: 'Not yet set', pending: true },
          { label: 'Rent', value: 'Not yet set', pending: true },
        ],
      },
    ],
  },
  versions: [],
  selectedVersionId: '',
  recipients: [],
  activity: [],
  pending: [],
  fillOnBehalf: false,
  actions: [
    {
      id: 'builder',
      label: 'Open in builder',
      method: 'navigate',
      endpoint: '/applications/3d77f210-4444-4dd4-8f44-0a7f8cbe0004/lease',
      tone: 'primary',
    },
  ],
};

// ---------------------------------------------------------------------------
// 5 — A condition report still being pulled from zInspector (Q3, T13)
// ---------------------------------------------------------------------------

export const conditionReportRow: SentItemRow = {
  itemId: 'condition_report:zi-88213',
  kind: 'condition_report',
  kindLabel: 'Move-in condition report',
  typeGroup: 'condition_report',
  subject: 'Hollis Grant',
  propertyLabel: '331 E Chestnut',
  unitLabel: 'B',
  caseId: 'case-grant',
  caseKind: 'household',
  groupId: null,
  state: 'draft',
  stateLabel: 'Not sent yet',
  lifecycle: {
    step: 'drafting',
    label: 'Being prepared',
    detail: 'Pulling from zInspector',
  },
  waitingOn: { party: 'system', names: [], label: 'Pulling from zInspector · 40 min' },
  since: ago(40 * MIN),
  inMotion: true,
  lastActivityAt: ago(40 * MIN),
  sentAt: null,
  completedAt: null,
  signals: NO_SIGNALS,
  digest: 'Move-in inspection · 331 E Chestnut B',
  versionCount: 0,
  viewable: false,
  resendable: false,
};

export const conditionReportDetail: SentItemDetailResponse = {
  row: conditionReportRow,
  panel: {
    digest: conditionReportRow.digest,
    groups: [
      {
        rows: [
          { label: 'Inspection', value: 'Move-in' },
          { label: 'Report', value: 'Still being pulled', pending: true },
        ],
      },
    ],
  },
  versions: [],
  selectedVersionId: '',
  recipients: [],
  activity: [
    { at: ago(40 * MIN), type: 'other', kind: 'sent', text: 'Found in zInspector, in progress' },
  ],
  pending: ['The inspection is still open in zInspector'],
  fillOnBehalf: false,
  actions: [],
};

// ---------------------------------------------------------------------------
// 6 — Executed, but the occupancy upload failed: still in motion, blocked
// ---------------------------------------------------------------------------

export const leaseSyncBlockedRow: SentItemRow = {
  itemId: 'lease:b6de0940-5555-4ee5-9055-0a7f8cbe0005',
  kind: 'lease',
  kindLabel: 'Lease',
  typeGroup: 'lease',
  subject: 'Sunny Oduya, Femi Oduya',
  propertyLabel: '77 S Roosevelt',
  unitLabel: '#12',
  caseId: 'case-oduya',
  caseKind: 'household',
  groupId: 'b6de0940-5555-4ee5-9055-0a7f8cbe0005',
  state: 'completed',
  stateLabel: 'Fully executed',
  lifecycle: {
    step: 'syncing',
    label: 'Filing in the portal',
    detail: 'The upload was rejected — the unit has no active occupancy.',
    blocked: true,
  },
  waitingOn: { party: 'system', names: [], label: 'Portal sync · 3 days' },
  since: ago(3 * DAY),
  inMotion: true,
  lastActivityAt: ago(3 * DAY),
  sentAt: ago(11 * DAY),
  completedAt: ago(3 * DAY),
  signals: { block: 1, attention: 0, notice: 0 },
  digest: '12 months from 15 Aug 2026 · $2,100/mo · 2 residents',
  versionCount: 1,
  viewable: true,
  resendable: false,
};

// ---------------------------------------------------------------------------
// 7 — Accommodation, and the stub an off-roster viewer would see instead
// ---------------------------------------------------------------------------

export const rraRow: SentItemRow = {
  itemId: 'rra_intake:case-8811',
  kind: 'rra_intake',
  kindLabel: 'Accommodation request',
  typeGroup: 'accommodation',
  subject: 'Requester on file',
  propertyLabel: '412 N Ash',
  unitLabel: null,
  caseId: 'case-8811',
  caseKind: 'household',
  groupId: null,
  state: 'in_progress',
  stateLabel: 'Under review',
  lifecycle: { step: 'countersign', label: 'With WHP', detail: 'Awaiting the staff decision' },
  waitingOn: { party: 'whp', names: [], label: 'WHP · 4 days' },
  since: ago(4 * DAY),
  inMotion: true,
  lastActivityAt: ago(4 * DAY),
  sentAt: ago(12 * DAY),
  completedAt: null,
  signals: NO_SIGNALS,
  digest: 'Assistance animal · verification returned',
  versionCount: 0,
  viewable: false,
  resendable: false,
};

/**
 * The withheld stub (§5.7). The list route OMITS restricted rows rather than
 * stubbing them, so this should never arrive — it exists because the renderer
 * is the safety net, and a net you never test is not a net.
 */
export const redactedRow: SentItemRow = {
  itemId: 'rra_intake:withheld',
  kind: 'rra_intake',
  kindLabel: 'Accommodation request',
  typeGroup: 'accommodation',
  subject: '',
  propertyLabel: null,
  unitLabel: null,
  caseId: null,
  caseKind: null,
  groupId: null,
  state: 'in_progress',
  stateLabel: '',
  lifecycle: { step: 'out', label: '' },
  waitingOn: null,
  since: null,
  inMotion: true,
  lastActivityAt: ago(DAY),
  sentAt: null,
  completedAt: null,
  signals: NO_SIGNALS,
  digest: '',
  versionCount: 0,
  viewable: false,
  resendable: false,
  redacted: true,
};

// ---------------------------------------------------------------------------
// 8 — A rental verification, one row per landlord (§5.1)
// ---------------------------------------------------------------------------

export const rentalVerificationRow: SentItemRow = {
  itemId: 'rental_verification:c4a1#lm-2',
  kind: 'rental_verification',
  kindLabel: 'Rental verification',
  typeGroup: 'verification',
  subject: 'Ridgeline Property Group (for Priya Raman)',
  propertyLabel: '2210 W Poplar',
  unitLabel: null,
  caseId: 'case-raman',
  caseKind: 'household',
  groupId: 'c4a1',
  state: 'sent',
  stateLabel: 'Sent',
  lifecycle: { step: 'out', label: 'With the landlord' },
  waitingOn: {
    party: 'recipient',
    names: ['Ridgeline Property Group'],
    label: 'Ridgeline Property Group · 40 days',
  },
  since: ago(40 * DAY),
  inMotion: true,
  lastActivityAt: ago(38 * DAY),
  sentAt: ago(40 * DAY),
  completedAt: null,
  signals: { block: 0, attention: 1, notice: 0 },
  digest: 'Previous landlord · no response yet',
  versionCount: 0,
  viewable: false,
  resendable: true,
};

// ---------------------------------------------------------------------------
// The corpus
// ---------------------------------------------------------------------------

/** Every fixture row, in the `all` view's order (lastActivityAt desc). */
export const FIXTURE_ROWS: SentItemRow[] = [
  leaseOutRow,
  leaseDraftRow,
  conditionReportRow,
  leaseSyncBlockedRow,
  rraRow,
  intakeRow,
  leaseDoneRow,
  rentalVerificationRow,
];

export const FIXTURE_DETAILS: Record<string, SentItemDetailResponse> = {
  [leaseOutRow.itemId]: leaseOutDetail,
  [intakeRow.itemId]: intakeDetail,
  [leaseDoneRow.itemId]: leaseDoneDetail,
  [leaseDraftRow.itemId]: leaseDraftDetail,
  [conditionReportRow.itemId]: conditionReportDetail,
};

/**
 * The list response the dev-only `?fixtures=1` mode renders.
 *
 * It does NOT re-sort or re-filter: the contract is that the server orders a
 * page and the client renders it in the order given (§6.1), and a fixture mode
 * that quietly sorts would hide exactly the bug that rule exists to prevent.
 */
export function fixtureListResponse(): SentItemsListResponse {
  const counts = new Map<string, number>();
  for (const row of FIXTURE_ROWS) {
    counts.set(row.typeGroup, (counts.get(row.typeGroup) ?? 0) + 1);
  }
  return {
    items: FIXTURE_ROWS,
    total: FIXTURE_ROWS.length,
    nextCursor: null,
    facets: {
      typeGroups: [...counts.entries()].map(([value, count]) => ({
        value,
        label: value,
        count,
      })),
      states: [],
      parties: [
        { value: 'whp', label: 'Waiting on WHP', count: 2 },
        { value: 'recipient', label: 'Waiting on a person', count: 3 },
        { value: 'system', label: 'With the system', count: 2 },
      ],
    },
    gaps: [],
    withheld: [],
    generatedAt: FIXTURE_NOW.toISOString(),
  };
}

/** The detail the dev-only fixtures mode serves for a row, when it has one. */
export function fixtureDetail(itemId: string): SentItemDetailResponse | null {
  return FIXTURE_DETAILS[itemId] ?? null;
}
